import React, { Fragment } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import AvailableContribution from 'src/components/Buy/BuyFlow/AvailableContribution/AvailableContribution';
import IRAContributionsAvailableTip from 'src/components/Buy/BuyFlow/IRAContributionsEditor/IRAContributionsAvailableTip';
import RadioButton from 'src/components/Shared/RadioButton/RadioButton';
import ToolTip from 'src/components/Shared/ToolTip/ToolTip';
import { moduleActions } from 'src/redux/modules';
import { updateExchangeContributionYear } from 'src/redux/modules/exchangeTransaction/actions';
import {
  getExchangeContributionYear,
  getExchangeCurrentYearIRABlockedMessage,
  getExchangePriorYearIRABlockedMessage,
} from 'src/redux/modules/exchangeTransaction/exchangeTransaction.selectors';
import {
  getIRALimit,
  getWindowCutoffDate,
  getWithinIRAPriorYearContributionPeriod,
} from 'src/redux/modules/funds/IRA/IRA.selectors';
import { getDisplayTradeDate } from 'src/redux/modules/funds/UserFunds/userFunds.selectors';
import {
  getCurrentContributionYear,
  getPriorContributionYear,
} from 'src/redux/modules/shoppingCart/shoppingCart.selectors';

import { getPersonalInformationData } from 'src/redux/modules/user/PersonalInformation/personalInformation.selectors';
import {
  AGREEMENT_TYPES,
  CURRENT_YEAR,
  PRIOR_YEAR,
} from 'src/settings/settings';
import { coalesce } from 'src/utils/utils';
import './ExchangeIRAAvailable.scss';

export interface ExchangeIRAAvailableProps {
  actions;
  className?: string;
}
const ExchangeIRAAvailable: React.VFC<ExchangeIRAAvailableProps> = ({
  actions,
  className = '',
}) => {
  const dispatch = useDispatch();
  const IRALimit = useSelector(getIRALimit);
  const currentContributionYear: string = useSelector(
    getCurrentContributionYear
  );
  const displayTradeDate = useSelector(getDisplayTradeDate);
  const exchangeContributionYear = useSelector(getExchangeContributionYear);
  const exchangeCurrentYearIRABlockedMessage: string = useSelector(
    getExchangeCurrentYearIRABlockedMessage
  );
  const exchangePriorYearIRABlockedMessage: string = useSelector(
    getExchangePriorYearIRABlockedMessage
  );
  const personalData = useSelector(getPersonalInformationData); // `personalData` used elsewhere consistently
  const priorContributionYear = useSelector(getPriorContributionYear);
  const windowCutoffDate = useSelector(getWindowCutoffDate);
  const withinIRAPriorYearContributionPeriod: boolean = useSelector(
    getWithinIRAPriorYearContributionPeriod
  );

  return (
    <div
      className={`${coalesce(className, '')} exchange-ira-available-container`}
    >
      <div className="exchange-ira-available-headers exchange-ira-available-contribution-headers">
        <div className="exchange-ira-available-year-header">
          Contribution Year
        </div>
        <div className="exchange-ira-available-available-header">
          <span className="exchange-ira-available-available-header-text">
            Available IRA Contribution
          </span>
          <ToolTip message={<IRAContributionsAvailableTip />} />
        </div>
      </div>
      {withinIRAPriorYearContributionPeriod && (
        <div className="exchange-ira-available-row">
          <div className="e2e-prior-year-selection exchange-ira-available-year-col">
            <RadioButton
              ariaLabel={coalesce(
                exchangePriorYearIRABlockedMessage,
                `Contribute to ${priorContributionYear} IRA`
              )}
              checked={exchangeContributionYear === PRIOR_YEAR}
              className="exchange-ira-available-radio"
              disabled={exchangePriorYearIRABlockedMessage !== null}
              labelText={
                <Fragment>
                  {priorContributionYear}
                  {exchangePriorYearIRABlockedMessage && (
                    <ToolTip message={exchangePriorYearIRABlockedMessage} />
                  )}
                </Fragment>
              }
              value={PRIOR_YEAR}
              onChange={() =>
                dispatch(updateExchangeContributionYear(PRIOR_YEAR))
              }
            />
          </div>
          <div className="exchange-ira-available-available-col">
            <AvailableContribution
              {...{
                IRALimit,
                actions,
                displayTradeDate,
                personalData,
                windowCutoffDate,
              }}
              agreementType={AGREEMENT_TYPES.TRADITIONAL_IRA}
              amount={coalesce(
                IRALimit.data.priorYearRemainingContributions,
                0
              )}
              className="exchange-ira-available-available-component"
              currentYear={false}
            />
          </div>
        </div>
      )}
      <div className="exchange-ira-available-row">
        <div className="exchange-ira-available-year-col">
          {withinIRAPriorYearContributionPeriod ? (
            <RadioButton
              ariaLabel={coalesce(
                exchangeCurrentYearIRABlockedMessage,
                `Contribute to ${currentContributionYear} IRA`
              )}
              checked={exchangeContributionYear === CURRENT_YEAR}
              className="radio"
              disabled={exchangeCurrentYearIRABlockedMessage !== null}
              labelText={
                <Fragment>
                  {currentContributionYear}
                  {exchangeCurrentYearIRABlockedMessage && (
                    <ToolTip message={exchangeCurrentYearIRABlockedMessage} />
                  )}
                </Fragment>
              }
              value={CURRENT_YEAR}
              onChange={() =>
                dispatch(updateExchangeContributionYear(CURRENT_YEAR))
              }
            />
          ) : (
            currentContributionYear
          )}
        </div>
        <div className="exchange-ira-available-available-col">
          <AvailableContribution
            currentYear
            {...{
              IRALimit,
              actions,
              displayTradeDate,
              personalData,
              windowCutoffDate,
            }}
            agreementType={AGREEMENT_TYPES.TRADITIONAL_IRA}
            amount={coalesce(
              IRALimit.data.currentYearRemainingContributions,
              0
            )}
            className="exchange-ira-available-available-component"
          />
        </div>
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(moduleActions, dispatch),
});

export default connect(null, mapDispatchToProps)(ExchangeIRAAvailable);
